import { createDocument } from "@tiptap/core"
import { prosemirrorToYXmlFragment } from "y-prosemirror"
import * as Y from "yjs"

import type { Content } from "@tiptap/core"
import type { Schema } from "@tiptap/pm/model"
import type { Transaction } from "@tiptap/pm/state"

/** The Y.XmlFragment field the editor binds to. */
export const SHARED_FIELD = "default"

const META = "meta"
const SEED_KEY = "seed"
const SAVED_KEY = "saved"
const SAVED_KEEP = 30

/** JSON with sorted keys, since a JSONB column hands keys back in its own order. */
function stableStringify(value: unknown): string {
	if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`
	if (value && typeof value === "object") {
		const entries = Object.entries(value as Record<string, unknown>)
			.filter(([, v]) => v !== undefined)
			.sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
		return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`).join(",")}}`
	}
	return JSON.stringify(value) ?? "null"
}

/** The same key for the same content, whether it is HTML or editor JSON. */
export function contentKey(content: Content): string {
	return typeof content === "string" ? content : stableStringify(content)
}

/** FNV-1a: a stable 32-bit number for a string, never 0. */
export function hash32(value: string): number {
	let hash = 0x811c9dc5
	for (let i = 0; i < value.length; i += 1) {
		hash ^= value.charCodeAt(i)
		hash = Math.imul(hash, 0x01000193)
	}
	return hash >>> 0 || 1
}

/** True for a transaction that only replays a teammate's edit from the shared doc. */
export function isRemoteChange(tr: Transaction): boolean {
	const meta = tr.getMeta("y-sync$") as { isChangeOrigin?: boolean } | undefined
	return !!meta?.isChangeOrigin
}

/** True once the doc holds the saved article, so pushing content into it replaces instead of adding. */
export function isSeeded(ydoc: Y.Doc): boolean {
	return ydoc.getMap<number>(META).has(SEED_KEY)
}

function holdsBody(fragment: Y.XmlFragment): boolean {
	return fragment
		.toArray()
		.some(
			(child) =>
				!(
					child instanceof Y.XmlElement &&
					child.nodeName === "paragraph" &&
					child.length === 0
				),
		)
}

/**
 * Fills a fresh shared doc with the saved article. Every editor that seeds the
 * same text writes it under the same Yjs client id, so two editors seeding at
 * once produce identical items that merge into one copy instead of two.
 * Returns the seed id this editor wrote, or null when the doc was already seeded.
 */
export function seedSharedDoc(
	ydoc: Y.Doc,
	schema: Schema,
	content: Content,
): number | null {
	const meta = ydoc.getMap<number>(META)
	if (meta.has(SEED_KEY)) return null
	// A body written before the seed ran (a setContent racing the editor's create) is the article already.
	if (holdsBody(ydoc.getXmlFragment(SHARED_FIELD))) {
		meta.set(SEED_KEY, ydoc.clientID)
		return null
	}
	const key = contentKey(content)
	const seedId = hash32(key)
	const seed = new Y.Doc()
	seed.clientID = seedId
	prosemirrorToYXmlFragment(
		createDocument(content, schema),
		seed.getXmlFragment(SHARED_FIELD),
	)
	seed.getMap<number>(META).set(SEED_KEY, seedId)
	Y.applyUpdate(ydoc, Y.encodeStateAsUpdate(seed), "seed")
	seed.destroy()
	// The saved content it started from is not an outside edit for anyone who joins later.
	rememberSaved(ydoc, content)
	return seedId
}

/**
 * Two editors that seeded different text at the same moment both landed in the
 * doc; the one whose seed lost the meta race takes its copy back out.
 */
export function watchSeedRace(ydoc: Y.Doc, seedId: number): () => void {
	const meta = ydoc.getMap<number>(META)
	const fragment = ydoc.getXmlFragment(SHARED_FIELD)
	const settle = () => {
		const winner = meta.get(SEED_KEY)
		if (winner === undefined || winner === seedId) return
		ydoc.transact(() => {
			const children = fragment.toArray()
			for (let i = children.length - 1; i >= 0; i -= 1) {
				const item = (children[i] as { _item?: { id: { client: number } } })
					._item
				if (item?.id.client === seedId) fragment.delete(i, 1)
			}
		})
		meta.unobserve(settle)
	}
	meta.observe(settle)
	settle()
	return () => meta.unobserve(settle)
}

/** Notes that this editor just saved `content`, so the row echoing back is not mistaken for an outside edit. */
export function rememberSaved(ydoc: Y.Doc, content: Content) {
	const saved = ydoc.getArray<number>(SAVED_KEY)
	ydoc.transact(() => {
		saved.push([hash32(contentKey(content))])
		if (saved.length > SAVED_KEEP) saved.delete(0, saved.length - SAVED_KEEP)
	})
}

/** True when some editor in the session saved exactly this content. */
export function wasSavedBySession(ydoc: Y.Doc, content: Content): boolean {
	return ydoc
		.getArray<number>(SAVED_KEY)
		.toArray()
		.includes(hash32(contentKey(content)))
}

/**
 * The one editor that applies an edit made outside the session (a restore, an
 * accepted suggestion, an API write), so it lands once instead of once per tab.
 */
export function isSessionLeader(
	ydoc: Y.Doc,
	connected: Iterable<number>,
): boolean {
	let lowest = ydoc.clientID
	for (const clientId of connected) lowest = Math.min(lowest, clientId)
	return lowest === ydoc.clientID
}
